import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore'
import Database from '../types/Database'
import { useTestDataStore } from '../stores/testDataStore'

export interface DiveStats {
  totalDives: number
  uniqueBuddies: number
  depthBreakdown: { tier: string; count: number }[]
  popularSites: { name: string; count: number }[]
  nextDive: Database.Dive | null
  scheduledCount: number
}

/**
 * Fetches aggregated dive stats for the current user.
 * Combines hosted dives + joined dives (approved join requests).
 */
export async function getUserDiveStats(userId: string): Promise<DiveStats> {
  const db = getFirestore()
  const showTestData = useTestDataStore.getState().showTestData

  // 1. Get dives the user hosted
  const divesCol = collection(db, 'dives')
  const hostedQuery = query(divesCol, where('hostId', '==', userId))
  const hostedSnap = await getDocs(hostedQuery)
  const hostedDives: Database.Dive[] = []
  hostedSnap.forEach(d => {
    const dive = { id: d.id, ...d.data() } as Database.Dive
    const isTestDive = (dive as any).testData === true
    if (showTestData === isTestDive) hostedDives.push(dive)
  })

  // 2. Get dives the user joined (approved join requests)
  const joinCol = collection(db, 'joinRequests')
  const joinQuery = query(
    joinCol,
    where('requesterId', '==', userId),
    where('status', '==', 'approved')
  )
  const joinSnap = await getDocs(joinQuery)

  const joinedDives: Database.Dive[] = []
  for (const jDoc of joinSnap.docs) {
    const jr = jDoc.data() as Omit<Database.JoinRequest, 'id'>
    const diveRef = doc(db, 'dives', jr.diveId)
    const diveSnap = await getDoc(diveRef)
    if (diveSnap.exists()) {
      const dive = { id: diveSnap.id, ...diveSnap.data() } as Database.Dive
      const isTestDive = (dive as any).testData === true
      if (showTestData === isTestDive) joinedDives.push(dive)
    }
  }

  // 3. Combine and deduplicate
  const allDivesMap = new Map<string, Database.Dive>()
  for (const d of [...hostedDives, ...joinedDives]) {
    allDivesMap.set(d.id, d)
  }
  const allDives = Array.from(allDivesMap.values())

  // 4. Compute stats
  const now = new Date()

  // Unique buddies: collect all host IDs from joined dives + all approved requesters from hosted dives
  const buddyIds = new Set<string>()
  for (const d of joinedDives) {
    if (d.hostId !== userId) buddyIds.add(d.hostId)
  }
  // Get approved requesters for hosted dives
  for (const d of hostedDives) {
    const approvedQ = query(
      joinCol,
      where('diveId', '==', d.id),
      where('status', '==', 'approved')
    )
    const approvedSnap = await getDocs(approvedQ)
    approvedSnap.forEach(aDoc => {
      const req = aDoc.data() as Omit<Database.JoinRequest, 'id'>
      if (req.requesterId !== userId) buddyIds.add(req.requesterId)
    })
  }

  // Depth breakdown
  const depthCounts: Record<string, number> = { '18m': 0, '30m': 0, '30m+': 0 }
  for (const d of allDives) {
    if (d.depthTier in depthCounts) depthCounts[d.depthTier]++
  }
  const depthBreakdown = Object.entries(depthCounts)
    .map(([tier, count]) => ({ tier, count }))
    .sort((a, b) => b.count - a.count)

  // Popular sites
  const siteCounts: Record<string, number> = {}
  for (const d of allDives) {
    siteCounts[d.siteName] = (siteCounts[d.siteName] || 0) + 1
  }
  const popularSites = Object.entries(siteCounts)
    .map(([name, count]) => ({ name, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5)

  // Upcoming / scheduled dives (future, not cancelled)
  const upcoming = allDives
    .filter(
      d =>
        d.dateTime.toDate() > now &&
        d.status !== 'cancelled' &&
        d.status !== 'completed'
    )
    .sort(
      (a, b) => a.dateTime.toDate().getTime() - b.dateTime.toDate().getTime()
    )

  return {
    totalDives: allDives.length,
    uniqueBuddies: buddyIds.size,
    depthBreakdown,
    popularSites,
    nextDive: upcoming[0] ?? null,
    scheduledCount: upcoming.length
  }
}
